import { FC, useState } from "react";
import { useRouter } from "next/router";
import { Bug } from "lucide-react";

import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { clsxMerge } from "shared/lib/helpers";

import { BugReportDialog } from "@/modules/bug-reports/bug-report-dialog";
import { useFetchBugReportsSwitch } from "@/modules/bug-reports/data/queries";
import { BugReportContextI } from "@/modules/bug-reports/lib/form";
import { useListsStore } from "@/modules/list-workspace/store/lists-store";

/**
 * Where the caller is: the page, plus the open list or campaign. The page link
 * has no query or fragment, because a query can hold contact details.
 */
const useBugReportContext = (): (() => BugReportContextI) => {
  const router = useRouter();

  return () => {
    const isOnLists = router.pathname.startsWith("/lists");
    const campaignId = router.query.campaign_id;

    return {
      pageURL: `${window.location.origin}${window.location.pathname}`,
      listId: isOnLists ? useListsStore.getState().activeListId : undefined,
      campaignId: typeof campaignId === "string" ? campaignId : undefined,
    };
  };
};

/**
 * The Submit bug button (GLE-8483). It shows only while the backend says bug
 * reports are on (`GET /user/v1/bug_reports/status`, `enabled`).
 */
export const BugReportNavButton: FC<PropsWithClassNameI> = ({ className }) => {
  const { data: isOn } = useFetchBugReportsSwitch();
  const readContext = useBugReportContext();
  const [context, setContext] = useState<BugReportContextI | null>(null);

  if (isOn !== true) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        title="Submit a bug"
        aria-label="Submit a bug"
        className={clsxMerge(
          "group btn btn-ghost relative px-3 py-2 text-white opacity-80",
          "hover:border-[#F88167]/50 hover:bg-[#F88167]/20 hover:opacity-100",
          className
        )}
        onClick={() => setContext(readContext())}
      >
        <Bug size={24} />
      </button>

      {context && (
        <BugReportDialog context={context} onClose={() => setContext(null)} />
      )}
    </>
  );
};
